import axios, { AxiosResponse } from 'axios';
import { loadConfig } from '@api/api';
import { SeriesRes } from '@models/Series';
import { isEmpty } from 'lodash';
import { ReCTFFR, ReGenerate_data, ReSegmentation } from './jobqueues';

const getSeriesApi = async () => {
  const BASE_URL = await loadConfig();
  return `${BASE_URL}/api/v1/series`;
};

export type SeriesParameterChamber4Body = {
  SavedInfo: {
    Chamber: {
      Chamber4: number[];
    };
  };
};

export type SeriesParameterCoronaryReRunCTFFRBody = {
  SavedInfo: {
    Coronary: {
      ReRunCTFFR: boolean;
    };
  };
};

export type SeriesParameterChamberCaptureProp = {
  version: 1;
  viewportId: string;
  seriesId: string;
  chamberViewProps: unknown;
};

export type SeriesParameterChamberCaptureProps = Record<
  string,
  SeriesParameterChamberCaptureProp
>;

export type SeriesParameterChamberCapturePropsBody = {
  chamberCaptureProps: SeriesParameterChamberCaptureProps;
};

export const buildSeriesParameterChamber4Body = (
  chamber4: number[],
): SeriesParameterChamber4Body => ({
  SavedInfo: {
    Chamber: {
      Chamber4: [...chamber4],
    },
  },
});

export const buildSeriesParameterCoronaryReRunCTFFRBody = (
  reRunCTFFR = true,
): SeriesParameterCoronaryReRunCTFFRBody => ({
  SavedInfo: {
    Coronary: {
      ReRunCTFFR: reRunCTFFR,
    },
  },
});

export const buildSeriesParameterChamberCapturePropsBody = (
  chamberCaptureProps: SeriesParameterChamberCaptureProps,
): SeriesParameterChamberCapturePropsBody => ({
  chamberCaptureProps: { ...chamberCaptureProps },
});

export const postSeriesParameterChamber4 = async ({
  accessToken,
  seriesId,
  chamber4,
}: {
  accessToken: string;
  seriesId: string;
  chamber4: number[];
}) => {
  const SERIES_API = await getSeriesApi();

  const res: AxiosResponse = await axios({
    method: 'post',
    url: `${SERIES_API}/parameter/${seriesId}`,
    headers: {
      Accept: '*/*',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    data: buildSeriesParameterChamber4Body(chamber4),
  });

  return res;
};

export const postSeriesParameterCoronaryReRunCTFFR = async ({
  accessToken,
  seriesId,
  reRunCTFFR = true,
}: {
  accessToken: string;
  seriesId: string;
  reRunCTFFR?: boolean;
}) => {
  const SERIES_API = await getSeriesApi();

  const res: AxiosResponse = await axios({
    method: 'post',
    url: `${SERIES_API}/parameter/${seriesId}`,
    headers: {
      Accept: '*/*',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    data: buildSeriesParameterCoronaryReRunCTFFRBody(reRunCTFFR),
  });

  return res;
};

export const postSeriesParameterChamberCaptureProps = async ({
  accessToken,
  seriesId,
  chamberCaptureProps,
}: {
  accessToken: string;
  seriesId: string;
  chamberCaptureProps: SeriesParameterChamberCaptureProps;
}) => {
  const SERIES_API = await getSeriesApi();

  const res: AxiosResponse = await axios({
    method: 'post',
    url: `${SERIES_API}/parameter/${seriesId}`,
    headers: {
      Accept: '*/*',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    data: buildSeriesParameterChamberCapturePropsBody(chamberCaptureProps),
  });

  return res;
};

export const getSeriesByStudy = async ({
  accessToken,
  params = { id: '' },
}: {
  accessToken: string;
  params?: {
    id: string;
  };
}) => {
  const Series_API = await getSeriesApi();

  const res: AxiosResponse<SeriesRes> = await axios({
    method: 'get',
    url: `${Series_API}/study/${params.id}`,
    headers: {
      Accept: '*/*',
      Authorization: `Bearer ${accessToken}`,
    },
  })
    .then((res) => {
      console.log(res);
      return res;
    })
    .catch((err) => {
      console.log(err);
      return err;
    });

  return res;
};

export const exportSeries = async ({
  accessToken,
  id,
}: {
  accessToken: string;
  id: string;
}) => {
  const SERIES_API = await getSeriesApi();
  if (isEmpty(id)) return;
  const res: AxiosResponse = await axios({
    method: 'get',
    url: `${SERIES_API}/export/${id}`,
    headers: {
      Accept: '*/*',
      Authorization: `Bearer ${accessToken}`,
    },
    timeout: 300000,
    responseType: 'blob',
  })
    .then((res) => {
      console.log(res);
      return res;
    })
    .catch((err) => {
      console.log(err);
      return err;
    });

  return res;
};

export const ReSegmentationAPI = async ({
  accessToken,
  seriesIds,
}: {
  accessToken: string;
  seriesIds: string[];
}) => {
  const promises = seriesIds.map(async (id) => {
    const res = await ReSegmentation({ accessToken, seriesid:id });
    return {
      id,
      ...res,
    };
  });
  const results = await Promise.all(promises);
  return results;
}
export const ReCTFFRAPI = async ({
  accessToken,
  seriesIds,
}: {
  accessToken: string;
  seriesIds: string[];
}) => {
  const promises = seriesIds.map(async (id) => {
    await postSeriesParameterCoronaryReRunCTFFR({
      accessToken,
      seriesId: id,
      reRunCTFFR: true,
    });
    const res = await ReCTFFR({ accessToken, seriesid:id });
    return {
      id,
      ...res,
    };
  });
  const results = await Promise.all(promises);
  return results;
}

export const ReGenerateDataAPI = async ({
  accessToken,
  seriesIds,
}: {
  accessToken: string;
  seriesIds: string[];
}) => {
  const promises = seriesIds.map(async (id) => {
    const res = await ReGenerate_data({ accessToken, seriesid:id });
    return {
      id,
      ...res,
    };
  });
  const results = await Promise.all(promises);
  return results;
}

export const deleteSeriesAPI = async ({
  accessToken,
  seriesIds,
}: {
  accessToken: string;
  seriesIds: string[];
}) => {

  const promises = seriesIds.map(async (id) => {
  const res = await deleteSeriesById({ accessToken, id });
    return {
      id,
      ...res,
    };
  });

  const results = await Promise.all(promises);
  return results;
};

const deleteSeriesById = async ({
  accessToken,
  id,
}: {
  accessToken: string;
  id: string;
}) => {
  const SERIES_API = await getSeriesApi();
  if (isEmpty(id)) return;
  console.log(`${SERIES_API}/${id}`)
  const res: AxiosResponse = await axios({
    method: 'delete',
    url: `${SERIES_API}/${id}`,
    headers: {
      Accept: '*/*',
      Authorization: `Bearer ${accessToken}`,
    },
  })
    .then((res) => {
      console.log(res);
      return res;
    })
    .catch((err) => {
      console.log(err);
      return err;
    });

  return res;
};
